/**
 * Created by Ning Jiang on 2/7/2018.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {IWinRatioConfig, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {Timeline, TimelineLite, TweenLite} from "gsap";
import {EdisonGameConfig} from "../../EdisonGameConfig";
import {EdisonBigWinCountUp} from "./EdisonBigWinCountUp";

export class EdisonBigWinView extends BaseView {

    private _isShowing:boolean = false;
    private _dimmer:PIXI.Graphics;
    private _bigWinSpine:PIXI.spine.Spine;
    private _orientation:Orientation = Orientation.LANDSCAPE;
    private _currentLevel:WinRatioType = WinRatioType.BIG_WIN;
    private _currentScale:number = 1;
    private _layer:ScalableLayer;

    constructor() {
        super();
        this._layer = StageManager.getLayer(EdisonGameConfig.instance.LAYERS.BIG_WIN.name);
    }

    protected initAnimations():void {
        this._dimmer = new PIXI.Graphics();
        this._dimmer.alpha = 0;
        this._layer.addChild(this._dimmer);

        this._bigWinSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("bigWin"));

        this._layer.addChild(this._bigWinSpine);
        this.visible = false;
    }

    protected onResize(resizeData:IResizeData):void {
        this._orientation = resizeData.orientation;

        if(!this._isShowing) {
            this._isResizeDirty = true;
            return;
        }

        this._isResizeDirty = false;
        this.reposition(resizeData);

        this.visible = true;
    }

    private reposition(resizeData:IResizeData):void {
        const screenWidth:number = Math.ceil(resizeData.width / this._layer.scale.x);
        const screenHeight:number = Math.ceil(resizeData.height / this._layer.scale.y);
        const leftOffset:number = Math.floor(-this._layer.x / this._layer.scale.x);
        const topOffset:number = Math.floor(-this._layer.y / this._layer.scale.y);

        this._dimmer.clear();
        this._dimmer.beginFill(0x000000, 0.8);
        this._dimmer.drawRect(leftOffset, topOffset, screenWidth, screenHeight);

        // centered in the window.
        this._bigWinSpine.x = screenWidth / 2 + leftOffset;
        if(this._orientation === Orientation.LANDSCAPE) {
            this._bigWinSpine.y = screenHeight / 2 - screenHeight / 5.3;
        } else {
            this._bigWinSpine.y = screenHeight / 2 - screenHeight / 2.5;
        }

        this.setCurrentScale();
        this._bigWinSpine.scale.set(this._currentScale);
    }

    public getBigWinLevelAnimation(winRatioConfig:IWinRatioConfig):Timeline {
        this._currentLevel = winRatioConfig.type!;
        this._layer.alpha = 1;
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this.updateBigWinLevelAnimation(winRatioConfig.type!);
            },
            TweenLite.to(this._dimmer, 0.1, {alpha : 1}),
        ], winRatioConfig.type === WinRatioType.SUPER_MEGA_WIN ? .1 : 0);

        return timeline;
    }

    private updateBigWinLevelAnimation(ratio:WinRatioType, abort:boolean = false):TimelineLite {
        this._bigWinSpine.visible = true;

        let skinName:string = "BigWin";

        let tl:TimelineLite = new TimelineLite();

        this.setCurrentScale();

        if(ratio === WinRatioType.BIG_WIN) {
            skinName = "BigWin";
            this._bigWinSpine.scale.set(this._currentScale);
        } else if(ratio === WinRatioType.MEGA_WIN) {
            skinName = "MegaWin";
            this._bigWinSpine.scale.set(this._currentScale);
        } else {
            skinName = "SuperMega";
            this._bigWinSpine.scale.set(this._currentScale)
        }

        tl.add(() => {
            this._bigWinSpine.skeleton.setSkinByName(skinName);
            if(!abort)
                this._bigWinSpine.state.setAnimation(0, "InitialBigWin", false);
        });

        this.reposition(this._resizeData);

        tl.add(() => {
            this._bigWinSpine.state.setAnimation(0, "BigWin", true);
        }, abort ? 0 : 1);

        this._isShowing = true;

        return tl;
    }

    private setCurrentScale():void {
        let isLandscape:boolean = this._orientation === Orientation.LANDSCAPE;
        if(this._currentLevel === WinRatioType.BIG_WIN) {
            this._currentScale = isLandscape ? .9 : .8;
            this._bigWinSpine.scale.set(this._currentScale);
        } else if(this._currentLevel === WinRatioType.MEGA_WIN) {
            this._currentScale = isLandscape ? .9 : .75;
            this._bigWinSpine.scale.set(this._currentScale);
        } else {
            this._currentScale = isLandscape ? .9 : .65;
            this._bigWinSpine.scale.set(this._currentScale)
        }
    }

    public playEndingAnimation(endWinRatioConfig:IWinRatioConfig, isAborted:boolean):void {
        const timeline:TimelineLite = new TimelineLite();

        if(isAborted) {
            timeline.add(() => {
                this._currentLevel = endWinRatioConfig.type!
                this.updateBigWinLevelAnimation(endWinRatioConfig.type!, isAborted)
            });
        }

        timeline.add(() => {
            EventHandler.dispatchEvent(new GameEvent(EdisonBigWinCountUp.SET_RATIO, -1));
            this._isShowing = false;
            this._currentLevel = WinRatioType.BIG_WIN;
            this._bigWinSpine.state.setEmptyAnimation(0, 0);
            this._bigWinSpine.visible = false;
            this._dimmer.alpha = 0;
        }, 0.8);

        timeline.play();
    }
}